import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import { Trans } from '@lingui/react';
import MetaTags from '@components/MetaTags';
import ProgramVideoPlayerLayout from '@ecosystems/programs/ProgramVideoPlayerLayout';
import ProgramsBreadcrumb from '@ecosystems/programs/ProgramsBreadcrumb';
import ProgramsSubHeader from '@ecosystems/navigations/ProgramsSubHeader';
import EmptyProgram from '@skeletons/ProgressPageSkeleton';
import AccessProgramHeaderSection from '@ecosystems/programs/AccessProgramHeaderSection';
import MoodTrackerOverlay from '@ecosystems/programs/MoodTrackerOverlay';
import GuestProgramHeader from '@ecosystems/programs/GuestProgramHeaderSection';
import VideoPlayerSection from '@ecosystems/programs/VideoPlayerWindow';
import ProgramDocumentWindow from '@ecosystems/programs/DocumentWindow';
import SurveyOverlay from '@ecosystems/programs/SurveyOverlay';
import { canBuyProgramGift, canExtendProgramAccess } from '@lib/helpers';
import {
  CurrentUser,
  GenericProgram,
  ProgramAccessStatus,
  ProgramElement,
  ProgramSurvey,
  ProgramWeek,
  WeeklyProgram,
  WeeklyProgramQuery,
  useSaveGenericProgramProgressMutation,
} from '@gql/generated';
import { UserContext } from '@lib/contexts/UserProvider';
import LanguageContext from '@lib/contexts/languageContext';
import { useRouter } from 'next/router';
import ProgramExpirationBanner from '@ecosystems/programs/ProgramExpirationBanner';
import {
  GetFullGenericProgramDocument,
  useWeeklyProgramQuery,
  WeeklyProgramDocument,
} from '@gql/generated';
import SurveyElementWindow from '@ecosystems/programs/SurveyElementWindow';
import TextElementWindow from '@ecosystems/programs/TextElementWindow';
import { CHALLENGE_PROGRAMS } from '@pages/[lang]/challenges';
import LiveEventElementWindow from '@ecosystems/programs/LiveEventElementWindow';
import { WeeklySteps } from '@ecosystems/programs/WeeklySteps';

const Layout = styled(ProgramVideoPlayerLayout)`
  --bg-color-primary: #fff;
  --bg-color-secondary: #fff;

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    --bg-color-primary: #fff;
    --bg-color-secondary: #f1f1f1;
  }
`;

export const getStartedLink = ({ lang, slug, category }) => {
  if (CHALLENGE_PROGRAMS.includes(slug)) {
    return `/${lang}/challenges/${slug}/get-started`;
  }

  return `/${lang}/${category}/${slug}/get-started`;
};

type Props = {
  query: { [key: string]: string };
  params: { stepId: string; videoId: string };
  baseProgram: GenericProgram;
  program: WeeklyProgramQuery['weeklyProgram'];
};

type RenderMainWindowParams = {
  currentElement: ProgramElement;
  programId: string;
  currentSurvey: ProgramSurvey;
  currentUser: CurrentUser;
  currentWeek: ProgramWeek;
  hasAccess: boolean;
};

const WeeklyProgramProgress = (props: Props) => {
  const { query } = useRouter();
  const lang = useContext(LanguageContext);
  const { programId } = query;
  const { stepId: stepSlug, videoId: videoSlug } = props.params;
  const { baseProgram, program: ssrProgram } = props;

  const client = useApolloClient();
  const [saveGenericProgramProgress] = useSaveGenericProgramProgressMutation();
  const [currentUser, { loading: userLoading }] = useContext(UserContext);
  const {
    data,
    loading,
    error,
    refetch: refetchWeeklyProgram,
  } = useWeeklyProgramQuery({
    variables: { slug: programId as string },
    ssr: false,
  });

  function reloadProgram() {
    refetchWeeklyProgram({
      slug: props.query.programId as string,
    });
  }

  function reloadGenericProgram() {
    client.query({
      query: GetFullGenericProgramDocument,
      variables: { slug: props.query.programId },
      fetchPolicy: 'network-only',
    });
  }

  function renderSurvey({ survey, user_id, program_slug }) {
    return (
      <SurveyOverlay
        survey={survey}
        user_id={user_id}
        program_slug={program_slug}
        onComplete={() => {
          reloadGenericProgram();
          reloadProgram();
        }}
      />
    );
  }

  function renderMoodTracker(currentElement) {
    if (currentElement?.preMoodTracker?.finished !== true) {
      return (
        <MoodTrackerOverlay
          title={<Trans id="WeeklyProgram.moodtracker.pre.title" />}
          description={<Trans id="WeeklyProgram.moodtracker.pre.description" />}
          modalTitle={<Trans id="phasedProgram.moodtracker_modal.pre.title" />}
          modalDesc={
            <Trans id="phasedProgram.moodtracker_modal.pre.description" />
          }
          start_button_text={
            <Trans id="WeeklyProgram.moodtracker.pre.start_button" />
          }
          moodTracker={currentElement['preMoodTracker']}
          onComplete={() => {
            reloadProgram();
          }}
        />
      );
    } else if (
      currentElement.watched === true &&
      currentElement?.postMoodTracker?.finished !== true
    ) {
      return (
        <MoodTrackerOverlay
          title={<Trans id="WeeklyProgram.moodtracker.post.title" />}
          description={
            <Trans id="WeeklyProgram.moodtracker.post.description" />
          }
          modalTitle={<Trans id="phasedProgram.moodtracker_modal.post.title" />}
          modalDesc={
            <Trans id="phasedProgram.moodtracker_modal.post.description" />
          }
          start_button_text={
            <Trans id="WeeklyProgram.moodtracker.post.start_button" />
          }
          moodTracker={currentElement['postMoodTracker']}
          onComplete={() => {
            reloadProgram();
          }}
        />
      );
    }
    return null;
  }

  function renderMainWindow(params: RenderMainWindowParams) {
    const {
      currentElement,
      programId,
      currentSurvey,
      currentUser,
      currentWeek,
      hasAccess,
    } = params;
    if (currentSurvey) {
      return renderSurvey({
        survey: currentSurvey,
        program_slug: programId,
        user_id: currentUser?.id,
      });
    } else if (currentElement?.__typename === 'ProgramDocument') {
      return (
        <ProgramDocumentWindow
          programTitle={baseProgram.title}
          programId={programId}
          currentElement={currentElement}
          hasAccess={hasAccess}
          programProgress={program?.progress}
          programQuery={WeeklyProgramDocument}
          programCategory={baseProgram.category}
        />
      );
    } else if (currentElement?.__typename === 'ProgramPartElement') {
      return (
        <TextElementWindow
          programTitle={baseProgram.title}
          programId={programId}
          programProgress={program?.progress}
          textElement={currentElement}
          programQuery={WeeklyProgramDocument}
          programCategory={baseProgram.category}
          hasAccess={hasAccess}
        />
      );
    } else if (currentElement?.__typename === 'ProgramLiveEvent') {
      if (currentElement?.liveEvent?.linkToRecordings?.length > 0) {
        const recording = currentElement?.liveEvent?.linkToRecordings?.find(
          ({ video }) => video?.identifier === query?.params[2]
        );

        if (!recording?.video) {
          return (
            <LiveEventElementWindow
              programId={programId}
              element={currentElement}
              programCategory={baseProgram.category}
              hasAccess={hasAccess}
            />
          );
        }

        return (
          <VideoPlayerSection
            programTitle={baseProgram?.title}
            programId={programId}
            weekTitle={currentWeek.label}
            currentElement={{
              id: currentElement?.id,
              slug: currentElement?.slug,
              title: currentElement?.title,
              customTitle: currentElement?.title,
              customDescription: currentElement?.description,
              video: recording?.video,
              locked: currentElement?.locked,
            }}
            hasAccess={hasAccess}
            programProgress={program?.progress}
            programQuery={WeeklyProgramDocument}
            weekDescription={currentWeek.description}
            programCategory={baseProgram?.category}
            onVideoComplete={() => {
              saveGenericProgramProgress({
                variables: {
                  videoPartId: null,
                  documentPartId: null,
                  programPartElementId: null,
                  liveEventPartId: currentElement.id,
                },
              });
            }}
          />
        );
      } else {
        return (
          <LiveEventElementWindow
            programId={programId}
            element={currentElement}
            programCategory={baseProgram.category}
            hasAccess={hasAccess}
          />
        );
      }
    } else if (currentElement?.__typename === 'ProgramSurveyElement') {
      return (
        <SurveyElementWindow
          programTitle={baseProgram.title}
          programId={programId}
          surveyElement={currentElement}
          programProgress={program?.progress}
          programQuery={WeeklyProgramDocument}
          programCategory={baseProgram.category}
          hasAccess={hasAccess}
        />
      );
    } else if (currentElement?.__typename === 'ProgramVideo') {
      if (
        // IF NO ACCESS (TRAILER)
        !hasAccess ||
        // IF VIDEO LOCKED (TRAILER)
        currentElement?.locked ||
        // IF VIDEO DOESN'T HAVE MOODTRACKERS
        !currentElement?.preMoodTracker ||
        // VIDEO REWATCH
        currentElement?.finished ||
        // IF VIDEO PREMOODTRACKER IS DONE AND NOT WATCHED YET
        (currentElement?.preMoodTracker?.finished === true &&
          !currentElement?.watched)
      ) {
        const description = currentWeek.description;

        return (
          <VideoPlayerSection
            programTitle={baseProgram?.title}
            programId={programId}
            weekTitle={currentWeek.label}
            currentElement={currentElement}
            hasAccess={hasAccess}
            programProgress={program?.progress}
            programQuery={WeeklyProgramDocument}
            weekDescription={description}
            programCategory={baseProgram?.category}
            onVideoComplete={() => {
              saveGenericProgramProgress({
                variables: {
                  videoPartId: currentElement.id,
                  documentPartId: null,
                  programPartElementId: null,
                  liveEventPartId: null,
                },
              });
            }}
          />
        );
      } else {
        return renderMoodTracker(currentElement);
      }
    } else {
      // something in the data is not right.
      return null;
    }
  }

  const program = data?.weeklyProgram || ssrProgram;

  if (error || (!program && (loading || userLoading))) {
    return <EmptyProgram />;
  }

  let currentWeek, currentElement, currentSurvey;
  if (stepSlug === 'outro') {
    // we handle outro cases first because special case
    currentElement = program?.['outro'].videos?.find(
      (programVideo) => programVideo.slug === videoSlug
    );
  } else if (program) {
    currentWeek =
      (program?.weeks as ProgramWeek[])?.find(
        (week) => week.slug === stepSlug
      ) || program.weeks[0];

    if (stepSlug === 'survey') {
      if (videoSlug === 'pre-survey') {
        currentSurvey = baseProgram.preSurvey;
      } else if (videoSlug === 'post-survey') {
        currentSurvey = baseProgram.postSurvey;
      }
    } else {
      currentElement =
        currentWeek.elements.find((elements) => elements.slug === videoSlug) ||
        currentWeek.elements[0];
    }
  }

  const userProgramAccess = baseProgram.programAccess;

  const userHasAccess = [
    ProgramAccessStatus['Active'],
    ProgramAccessStatus['ExpiringSoon'],
  ].includes(baseProgram?.accessStatus);

  const enableGiftPurchase = canBuyProgramGift(
    baseProgram?.price,
    baseProgram?.priceType
  );

  const canExtendAccess = canExtendProgramAccess(
    baseProgram?.price,
    baseProgram?.priceType,
    baseProgram.accessStatus
  );

  return (
    <>
      <MetaTags
        title={baseProgram.meta.title || baseProgram.title}
        description={baseProgram.meta.description || baseProgram.description}
        thumbnailUrl={baseProgram.meta.imageUrl || baseProgram.titleImageUrl}
      />
      <Layout
        breadCrumb={
          <ProgramsBreadcrumb
            currentPage={baseProgram.title}
            programCategory={baseProgram.category}
          />
        }
        headerSection={
          <>
            {[
              ProgramAccessStatus['ExpiringSoon'],
              ProgramAccessStatus['Expired'],
            ].includes(baseProgram?.accessStatus) ? (
              <ProgramExpirationBanner
                programSlug={baseProgram?.slug}
                price={baseProgram?.price}
                accessStatus={baseProgram?.accessStatus}
                programCategory={baseProgram.category}
                accessUntil={userProgramAccess?.accessUntil}
                priceType={baseProgram?.priceType}
                currentUser={currentUser}
              />
            ) : null}
            {!userHasAccess ? (
              <GuestProgramHeader
                title={baseProgram.title}
                description={
                  baseProgram.shortDescription || baseProgram.description
                }
                instructors={baseProgram.instructors}
                trailer={baseProgram.trailer}
                programImage={baseProgram.titleImageUrl}
                assetURL={baseProgram.summaryAsset}
                themes={baseProgram.themes}
                programCategory={baseProgram.category}
                price={baseProgram.price}
                priceType={baseProgram.priceType}
                discount={baseProgram.discount}
                programSlug={baseProgram.slug}
                duration={baseProgram.duration}
                enableGiftPurchase={enableGiftPurchase}
                getStartedLink={getStartedLink({
                  lang,
                  slug: baseProgram.slug,
                  category: baseProgram.category,
                })}
              />
            ) : (
              <AccessProgramHeaderSection
                title={baseProgram.title}
                description={
                  baseProgram.shortDescription || baseProgram.description
                }
                instructors={program['instructors']}
                currentVideo={program['currentElement']}
                progress={program.progress}
                programsAccess={userProgramAccess}
                themes={baseProgram.themes}
                duration={baseProgram.duration}
                assetURL={baseProgram.summaryAsset}
                programCategory={baseProgram.category}
                enableGiftPurchase={enableGiftPurchase}
                canExtendAccess={canExtendAccess}
              />
            )}
          </>
        }
        subHeader={
          <ProgramsSubHeader
            hasAccess={userHasAccess}
            programCategory={baseProgram.category}
            overviewLink={`/${lang}/${baseProgram.category}/${program.slug}/overview`}
            progressLink={
              userHasAccess
                ? `/${lang}/${baseProgram.category}/${program['currentElement']?.path}`
                : null
            }
            showListingLink={false}
          />
        }
        mainVideo={renderMainWindow({
          currentElement,
          programId: programId as string,
          currentSurvey,
          currentUser,
          currentWeek,
          hasAccess: userHasAccess,
        })}
        steps={() => {
          return (
            <WeeklySteps
              programCategory={baseProgram?.category}
              program={program}
              programId={programId as string}
              stepSlug={stepSlug}
              videoSlug={videoSlug}
            />
          );
        }}
      />
    </>
  );
};

export default WeeklyProgramProgress;
